import React, { useState, useEffect } from 'react';
import {
  createProtobufRpcClient,
  SigningStargateClient,
  setupDistributionExtension,
} from '@cosmjs/stargate';
import _ from 'lodash';
import { QueryClientImpl } from 'honeywood-protobuf-js/types/bears/query';
import { useStores } from '@stores';

// code get from https://github.com/DonRai/react-image-webp/blob/master/modules/utils/index.js
let support;

/**
 * Check browser webp support
 * @returns {boolean}
 */
export const isWebpSupported = () => {
  if (typeof support !== 'undefined') return support;

  const elem = typeof document === 'object' ? document.createElement('canvas') : {};

  support = elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;

  return support;
};

export const gtagEvent = (event) => {
  if (process.env.REACT_APP_STAGE !== 'production') {
    return () => {};
  }
  return window.gtag('event', event, {
    PATHNAME: window.location.pathname,
  });
};

export const copyText = async ({ text }) => {
  const textField = document.createElement('textarea');
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
};

export const useCopied = (changedCopy = false) => {
  const [copiedState, setCopiedState] = React.useState(changedCopy);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (copiedState) {
        setCopiedState(false);
      }
    }, 2000);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [copiedState]);

  const setCopied = () => {
    setCopiedState(true);
  };

  return {
    copied: copiedState,
    setCopied,
  };
};

export const createAnonymousClient = async (url) => {
  const client = await SigningStargateClient.connectWithSigner(url);
  const rpcClient = createProtobufRpcClient(client.queryClient);
  const queryService = new QueryClientImpl(rpcClient);

  client.queryClient.distribution = setupDistributionExtension(client.queryClient).distribution;

  return {
    cosmos: client,
    bearsQueries: queryService,
  };
};

export const numberWithSeparator = (
  number,
  separatorForIntegerPart = ',',
  dividerOfNumberParts = '.',
) => {
  const splittedNumberArray = number.toString().split(dividerOfNumberParts);
  const result =
    splittedNumberArray.length > 1
      ? `${splittedNumberArray[0].replace(/\B(?=(\d{3})+(?!\d))/g, separatorForIntegerPart)}.${
          splittedNumberArray[1]
        }`
      : splittedNumberArray[0].replace(/\B(?=(\d{3})+(?!\d))/g, separatorForIntegerPart);
  return result;
};

export const getShortAddress = (address) => {
  return [...address.slice(0, 7), '...', address.slice(-12)];
};

export function useMediaQuery(query) {
  const stores = useStores();
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => {
      setMatches(media.matches);
    };

    if (
      stores.browserInfo &&
      stores.browserInfo.name &&
      stores.browserInfo.version &&
      !stores.browserInfo.isOldBrowser
    ) {
      media.addEventListener('change', listener);
      return () => {
        media.removeEventListener('change', listener);
      };
    }
    /* Don't remove this deprecated code! It's for supporting old browsers */
    media.addListener(listener);
    return () => {
      media.removeListener(listener);
    };
  }, [matches, query]);

  return matches;
}

export const floorRounding = (value, precision = 6) => {
  return value ? _.floor(value, precision) : undefined;
};

export const isLessThanOneThousandth = (value) => {
  if (value < 0.001) {
    return '<0,001';
  }
  return floorRounding(value, 3);
};

export const findAttribute = (logs, eventType, attributeKey) => {
  const txEvents = logs.map((log) => {
    const txEvent = log.events.find((event) => event.type === eventType);
    const txAttributes = txEvent
      ? txEvent.attributes.filter((attribute) => attribute.key === attributeKey)
      : undefined;
    return txAttributes ? txAttributes.map((attribute) => attribute.value) : undefined;
  });
  return txEvents;
};

export const findEvent = (logs, eventType) => {
  const txEvents = logs.map((log) => {
    const txEvent = log.events.find((event) => event.type === eventType);
    return txEvent?.attributes;
  });
  return txEvents;
};

export const splitCamelCase = (str) =>
  str.replace(/[A-Z]/g, (letter) => ` ${letter.toLowerCase()}`);

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export default {
  isWebpSupported,
  gtagEvent,
  copyText,
  useCopied,
  createAnonymousClient,
  numberWithSeparator,
  getShortAddress,
  useMediaQuery,
  isLessThanOneThousandth,
  floorRounding,
  findAttribute,
  splitCamelCase,
  findEvent,
  capitalizeFirstLetter,
};
